"use client";

import {
  Check,
  Lock,
  type LucideIcon,
  Reply,
  SkipForward,
  UserPlus,
} from "lucide-react";
import { createContext, type JSX, type ReactNode, useContext } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ResponseButtonsContextValue = {
  principalLabel: string;
};

const ResponseButtonsContext =
  createContext<ResponseButtonsContextValue | null>(null);

function useResponseButtonsContext(): ResponseButtonsContextValue {
  const context = useContext(ResponseButtonsContext);
  if (context === null) {
    throw new Error(
      "ResponseButtons sub-components must be used within ResponseButtons.Root",
    );
  }
  return context;
}

function Root({
  principalFirstName,
  className,
  children,
}: {
  principalFirstName?: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const principalLabel = principalFirstName
    ? `Principal ${principalFirstName}`
    : "the principal";

  return (
    <ResponseButtonsContext.Provider value={{ principalLabel }}>
      <div className={cn("flex w-full flex-col", className)}>{children}</div>
    </ResponseButtonsContext.Provider>
  );
}

function SkipButton({
  onClick,
  className,
  iconClassName,
  children,
}: {
  onClick: () => void;
  className?: string;
  iconClassName?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={cn("h-auto border-border text-sm", className)}
    >
      <SkipForward
        className={cn("h-4 w-4", iconClassName)}
        aria-hidden="true"
      />
      {children}
    </Button>
  );
}

function InterestButton({
  onClick,
  className,
  children,
}: {
  onClick: () => void;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Button
      onClick={onClick}
      className={cn(
        "h-auto bg-brand-purple text-sm font-semibold text-primary-foreground hover:bg-brand-purple-hover",
        className,
      )}
    >
      <Check className="h-4 w-4" aria-hidden="true" />
      {children}
    </Button>
  );
}

function Actions({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={cn("flex gap-2", className)}>{children}</div>;
}

function SkippingNote({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-1.5 text-xs text-ink-subtle",
        className,
      )}
    >
      <Lock className="h-3 w-3" aria-hidden="true" />
      Skipping is private to you
    </div>
  );
}

function Notes({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn("mt-2.5 flex flex-col gap-1.5", className)}>
      {children}
    </div>
  );
}

function NoteRow({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start gap-1.5 text-xs text-ink-subtle leading-relaxed">
      <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function InterestNote(): JSX.Element {
  const { principalLabel } = useResponseButtonsContext();
  return (
    <NoteRow icon={UserPlus}>
      &ldquo;I&apos;m interested&rdquo; sends a connection request to{" "}
      {principalLabel} — they&apos;ll see your profile and can reply.
    </NoteRow>
  );
}

function InvitationInterestNote(): JSX.Element {
  const { principalLabel } = useResponseButtonsContext();
  return (
    <NoteRow icon={Reply}>
      &ldquo;I&apos;m interested&rdquo; lets {principalLabel} reply directly and
      shares your profile so they can follow up.
    </NoteRow>
  );
}

function PrivacyNote(): JSX.Element {
  const { principalLabel } = useResponseButtonsContext();
  return (
    <NoteRow icon={Lock}>
      {`Skipping is private — ${principalLabel} won't be notified, and you can still apply to jobs here later.`}
    </NoteRow>
  );
}

export const ResponseButtons = {
  Root,
  Actions,
  SkipButton,
  InterestButton,
  SkippingNote,
  Notes,
  InterestNote,
  InvitationInterestNote,
  PrivacyNote,
};
